/**
 * The workbench's cascade list — source pinned as row 0, then every affected
 * activity in cascade (chronological) order, split into a table pane and a
 * sectioned-off gantt pane with a draggable divider between them (drag to see
 * more of either). The gantt mirrors the dep editor's strip: fixed workday
 * columns with its own horizontal scroll (opens at the first row's start),
 * ghost bar = pre-change position, solid bar = simulated position, milestone
 * diamonds, dashed data-date line, and dependency arrows routed by the dep
 * editor's computeArrows. Row heights are measured off the table so the two
 * panes stay in lockstep. Clicking a row spotlights its dependency lines and
 * scrolls its bar into view.
 */
import React from "react";
import { usePanScroll } from "../../../../../hooks/usePanScroll";
import { useSyncedScroll } from "../../../../../hooks/useSyncedScroll";
import type {
  DepGraph,
  ExternalCandidate,
  TaktColorEntry,
  WorkbenchPred,
  WorkbenchResult,
  WorkbenchRow,
} from "../../../../../models";
import { shortFromPredType } from "../../../../../models";
import { isMilestoneTaskType } from "../../../../../utils/p6";
import type { PredType } from "../../../../../utils/p6";
import { formatDateMonthDay, formatSignedDays } from "../../../../../utils/format";
import type { WorkbenchRowLike } from "../CascadeWorkbenchModal";
import { computeArrows } from "../../../../pull-plan/depCore/DepGanttStrip/computeArrows";
import type { ActivityBox } from "../../../../pull-plan/depCore/DepGanttStrip/computeArrows";
import DepArrows from "../../../../pull-plan/depCore/DepGanttStrip/DepArrows/DepArrows";
import { calendarIndexFor } from "../../../../pull-plan/depCore/DepGanttStrip/DepGanttStrip";
import DateDelta from "../DateDelta/DateDelta";
import ConstraintBadge from "../../../ConstraintBadge/ConstraintBadge";
import DurationCell from "../DurationCell/DurationCell";
import PredChip from "./PredChip/PredChip";
import "./WorkbenchRows.css";

const DAY_PX = 16;
const DEFAULT_GANTT_W = 460;
const MIN_GANTT_W = 240;
const MAX_GANTT_W = 980;
/** The squeezed table keeps at least this much width. */
const MIN_TABLE_W = 380;
const FALLBACK_ROW_H = 44;

interface Props {
  result: WorkbenchResult;
  colors: Record<string, TaktColorEntry>;
  activityWorkCodeMap: Map<number, string>;
  sectionMap: Map<number, string>;
  zoneMap: Map<number, string>;
  readOnly: boolean;
  editedEdgeIds: Set<number>;
  editedDurationIds: Set<number>;
  /** Activities whose date was pushed past a constraint — flag the badge red. */
  violatedIds: Set<number>;
  onEdgeChange: (row: WorkbenchRowLike, pred: WorkbenchPred, patch: { type?: PredType; lagDays?: number }) => void;
  onEdgeRemove: (row: WorkbenchRowLike, pred: WorkbenchPred) => void;
  /** Re-point a predecessor at an activity picked from the schedule search. */
  onEdgeReplace?: (row: WorkbenchRowLike, pred: WorkbenchPred, external: ExternalCandidate) => void;
  /** Server-driven schedule activity search for the predecessor typeahead. */
  fetchExternals?: (query: string) => Promise<ExternalCandidate[]>;
  onDurationChange: (row: WorkbenchRow, days: number) => void;
}

// ---------------------------------------------------------------------------
// Workday axis — Mon-Fri columns at a fixed DAY_PX, like the dep editor.
// ---------------------------------------------------------------------------

interface Axis {
  days: string[];
  index: Map<string, number>;
}

function isoAddDays(iso: string, n: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function isWeekend(iso: string): boolean {
  const dow = new Date(iso + "T00:00:00Z").getUTCDay();
  return dow === 0 || dow === 6;
}

function buildAxis(result: WorkbenchResult): Axis | null {
  const dates: string[] = [];
  const push = (v: string | null | undefined) => {
    if (v) dates.push(v.slice(0, 10));
  };
  if (result.source) {
    push(result.source.old_start);
    push(result.source.new_start);
    push(result.source.old_end);
    push(result.source.new_end);
  }
  for (const row of [...result.context_rows, ...result.rows]) {
    push(row.old_start);
    push(row.new_start);
    push(row.old_end);
    push(row.new_end);
  }
  push(result.data_date);
  if (dates.length === 0) return null;
  dates.sort();
  let cur = isoAddDays(dates[0], -4);
  const last = isoAddDays(dates[dates.length - 1], 6);
  const days: string[] = [];
  const index = new Map<string, number>();
  while (cur <= last) {
    if (!isWeekend(cur)) {
      index.set(cur, days.length);
      days.push(cur);
    }
    cur = isoAddDays(cur, 1);
  }
  return { days, index };
}

/** x of a START date's column; weekend dates snap FORWARD to the next workday
 *  (work that starts on a Saturday begins Monday). */
function dateX(axis: Axis, iso: string | null | undefined): number | null {
  if (!iso) return null;
  const key = iso.slice(0, 10);
  const direct = axis.index.get(key);
  if (direct != null) return direct * DAY_PX;
  for (let i = 0; i < axis.days.length; i++) {
    if (axis.days[i] >= key) return i * DAY_PX;
  }
  return (axis.days.length - 1) * DAY_PX;
}

/** x for an END date — the dep strip's prior-workday snap (calendarIndexFor),
 *  so a Saturday finish doesn't stretch the bar through Monday. */
function dateXEnd(axis: Axis, iso: string | null | undefined): number | null {
  if (!iso) return null;
  const key = iso.slice(0, 10);
  const direct = axis.index.get(key);
  if (direct != null) return direct * DAY_PX;
  const i = calendarIndexFor(axis.days, key);
  return i < 0 ? null : i * DAY_PX;
}

// ---------------------------------------------------------------------------
// Unified entry list — source first, then rows — shared by both panes so the
// measured table heights map 1:1 onto the gantt rows. Fields are flattened so
// the gantt/arrow/scroll code never branches on the entry kind.
// ---------------------------------------------------------------------------

interface Entry {
  kind: "source" | "row";
  id: number;
  milestone: boolean;
  oldStart: string | null;
  newStart: string | null;
  oldEnd: string | null;
  newEnd: string | null;
  /** Cascade rows only; null for the source entry. */
  row: WorkbenchRow | null;
}

const rowEntry = (row: WorkbenchRow): Entry => ({
  kind: "row",
  id: row.activity_id,
  milestone: row.milestone || isMilestoneTaskType(row.task_type),
  oldStart: row.old_start,
  newStart: row.new_start,
  oldEnd: row.old_end,
  newEnd: row.new_end,
  row,
});

export interface WorkbenchRowsHandle {
  /** Select an activity's row and scroll it into view (table + gantt). */
  focusActivity: (activityId: number) => void;
}

const WorkbenchRows = React.forwardRef<WorkbenchRowsHandle, Props>(({
  result,
  colors,
  activityWorkCodeMap,
  sectionMap,
  zoneMap,
  readOnly,
  editedEdgeIds,
  editedDurationIds,
  violatedIds,
  onEdgeChange,
  onEdgeRemove,
  onEdgeReplace,
  fetchExternals,
  onDurationChange,
}, ref) => {
  const axis = React.useMemo(() => buildAxis(result), [result]);

  const entries = React.useMemo<Entry[]>(() => {
    const out: Entry[] = result.context_rows.map(rowEntry);
    const src = result.source;
    if (src) {
      out.push({
        kind: "source",
        id: src.activity_id,
        milestone: isMilestoneTaskType(src.task_type),
        oldStart: src.old_start,
        newStart: src.new_start,
        oldEnd: src.old_end,
        newEnd: src.new_end,
        row: null,
      });
    }
    out.push(...result.rows.map(rowEntry));
    return out;
  }, [result]);

  // ---- selection: click a row to spotlight its dependency lines -----------
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const handleRowClick = (id: number) => (e: React.MouseEvent) => {
    // Interactive children (chips, duration input, popovers) own their clicks.
    if ((e.target as HTMLElement).closest("button, input, a")) return;
    const key = String(id);
    setSelectedId((prev) => (prev === key ? null : key));
  };

  // ---- pane split (drag the divider to trade table for gantt) -------------
  // During the drag the width is written straight to the pane element so the
  // React tree isn't re-rendered per pointermove; state commits on release.
  const [ganttW, setGanttW] = React.useState(DEFAULT_GANTT_W);
  const ganttPaneRef = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef<{ startX: number; startW: number; pending: number } | null>(null);

  const clampGanttW = (next: number, splitW: number) =>
    Math.max(MIN_GANTT_W, Math.min(Math.min(MAX_GANTT_W, splitW - MIN_TABLE_W), next));

  const onDividerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startW: ganttW, pending: ganttW };
  };
  const onDividerPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const splitW = e.currentTarget.parentElement?.clientWidth ?? MAX_GANTT_W;
    const next = clampGanttW(
      dragRef.current.startW - (e.clientX - dragRef.current.startX),
      splitW,
    );
    dragRef.current.pending = next;
    if (ganttPaneRef.current) ganttPaneRef.current.style.width = `${next}px`;
  };
  const onDividerPointerUp = () => {
    if (dragRef.current) setGanttW(dragRef.current.pending);
    dragRef.current = null;
  };

  // ---- row-height sync: measure table rows, mirror them in the gantt ------
  const tablePaneRef = React.useRef<HTMLDivElement>(null);
  const rowRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const [rowHeights, setRowHeights] = React.useState<number[]>([]);

  // Imperative jump used by the header's "constraints violated" pill: select the
  // row (spotlights its dep lines + scrolls its gantt bar via the selectedId
  // effect) and bring the table row into view.
  React.useImperativeHandle(ref, () => ({
    focusActivity: (activityId: number) => {
      const idx = entries.findIndex((e) => e.id === activityId);
      if (idx < 0) return;
      setSelectedId(String(activityId));
      rowRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  }), [entries]);

  React.useLayoutEffect(() => {
    const measure = () => {
      // FRACTIONAL heights (getBoundingClientRect, not offsetHeight): offsetHeight
      // rounds to an integer, so sub-pixel row heights accumulate drift and the
      // gantt rows fall out of alignment with the table further down the list.
      const next = entries.map(
        (_e, i) => rowRefs.current[i]?.getBoundingClientRect().height ?? FALLBACK_ROW_H,
      );
      setRowHeights((prev) =>
        prev.length === next.length && prev.every((h, i) => h === next[i])
          ? prev
          : next,
      );
    };
    measure();
    // Observe each ROW, not just the pane — a row that grows (wrapped pred chips)
    // doesn't resize the pane, so a pane-only observer would miss it. (Same
    // approach as the dep editor's gantt strip.)
    const ro = new ResizeObserver(measure);
    rowRefs.current.forEach((el) => el && ro.observe(el));
    return () => ro.disconnect();
  }, [entries]);

  // Both panes scroll vertically in lockstep (dep-modal pattern); the gantt
  // pane also owns horizontal scroll, so its x-scrollbar sits at the bottom
  // of the VISIBLE pane — reachable regardless of list length. Either pane
  // can also be grab-panned (post-pan clicks are swallowed by the hook).
  useSyncedScroll(tablePaneRef, ganttPaneRef);
  usePanScroll(tablePaneRef);
  usePanScroll(ganttPaneRef);

  // ---- gantt geometry ------------------------------------------------------
  const innerW = axis ? axis.days.length * DAY_PX : 0;
  const rowTops = React.useMemo(() => {
    const tops: number[] = [];
    let acc = 0;
    for (let i = 0; i < entries.length; i++) {
      tops.push(acc);
      acc += rowHeights[i] ?? FALLBACK_ROW_H;
    }
    return { tops, total: acc };
  }, [entries, rowHeights]);

  // The edges shim depends only on the result — heights/drag must not rebuild it.
  const edgesShim = React.useMemo(() => {
    const edges: DepGraph["edges"] = {};
    const withSource = result.source
      ? [...result.rows, { activity_id: result.source.activity_id, preds: result.source.preds }]
      : result.rows;
    for (const row of withSource) {
      edges[String(row.activity_id)] = row.preds.map((p) => ({
        pred_id: String(p.pred_activity_id),
        type: shortFromPredType(p.pred_type as PredType),
        lag_hours: p.lag_hours,
        edge_id: p.edge_id,
        is_new: p.is_new,
        crosses: false,
        driving: p.driving,
      }));
    }
    return edges;
  }, [result]);

  // Arrow mechanics come straight from the dep editor's strip: the same
  // ActivityBox geometry feeds computeArrows (MS-Project-style routing,
  // negative-lag re-routes, milestone point anchors).
  const arrows = React.useMemo(() => {
    if (!axis) return [];
    const boxes = new Map<string, ActivityBox[]>();
    entries.forEach((e, i) => {
      const x1 = dateX(axis, e.newStart);
      const x2e = dateXEnd(axis, e.newEnd);
      if (x1 == null || x2e == null) return;
      const h = rowHeights[i] ?? FALLBACK_ROW_H;
      const center = rowTops.tops[i] + h / 2;
      if (e.milestone) {
        // Point box around the diamond so incoming arrows land on its apex
        // instead of slicing through the glyph.
        boxes.set(String(e.id), [{
          rowIndex: i,
          left: x1,
          right: x1,
          top: center - 9,
          bottom: center + 9,
          midY: center,
          driveStartX: x1,
          driveFinishX: x1,
          zoneKey: null,
        }]);
        return;
      }
      const x2 = x2e + DAY_PX;
      const barTop = center + 1;
      boxes.set(String(e.id), [{
        rowIndex: i,
        left: x1,
        right: x2,
        top: barTop,
        bottom: barTop + 11,
        midY: barTop + 5.5,
        driveStartX: x1,
        driveFinishX: x2,
        zoneKey: null,
      }]);
    });
    const graphShim: DepGraph = { activities: {}, edges: edgesShim, data_date: result.data_date };
    return computeArrows(graphShim, boxes);
  }, [axis, entries, edgesShim, result.data_date, rowTops, rowHeights]);

  // Open the gantt aligned to the first listed activity's bar (earliest of its
  // ghost and simulated starts); the data date is only the fallback.
  const scrolledRef = React.useRef(false);
  React.useEffect(() => {
    scrolledRef.current = false;
  }, [result]);
  React.useEffect(() => {
    if (scrolledRef.current || !axis || !ganttPaneRef.current) return;
    const first = entries[0];
    let anchor: string | null = null;
    if (first) {
      const starts = [first.oldStart, first.newStart].filter(Boolean) as string[];
      if (starts.length) anchor = starts.sort()[0];
    }
    const x = dateX(axis, anchor ?? result.data_date);
    ganttPaneRef.current.scrollLeft = Math.max(0, (x ?? 0) - 48);
    scrolledRef.current = true;
  }, [axis, entries, result]);

  // Selecting a row brings its bar into view (smooth, only when off-screen).
  React.useEffect(() => {
    if (!selectedId || !axis || !ganttPaneRef.current) return;
    const entry = entries.find((e) => String(e.id) === selectedId);
    if (!entry) return;
    const x1 = dateX(axis, entry.newStart);
    const x2 = dateXEnd(axis, entry.newEnd);
    if (x1 == null) return;
    const pane = ganttPaneRef.current;
    const visibleLeft = pane.scrollLeft;
    const visibleRight = visibleLeft + pane.clientWidth;
    const barRight = (x2 ?? x1) + DAY_PX;
    if (x1 >= visibleLeft + 16 && barRight <= visibleRight - 16) return;
    pane.scrollTo({ left: Math.max(0, x1 - pane.clientWidth / 3), behavior: "smooth" });
  }, [selectedId, axis, entries]);

  // ---- renderers -----------------------------------------------------------

  const renderTableEntry = (entry: Entry, i: number) => {
    const attachRef = (el: HTMLDivElement | null) => {
      rowRefs.current[i] = el;
    };
    const selected = selectedId === String(entry.id);
    const wc = activityWorkCodeMap.get(entry.id);
    const fill = (wc && colors[wc]?.fill) || "#666";

    if (entry.kind === "source") {
      const src = result.source!;
      return (
        <div
          key="source"
          className={`cwb-row cwb-row--source${selected ? " is-selected" : ""}`}
          ref={attachRef}
          onClick={handleRowClick(entry.id)}
        >
          <span className="cwb-col-ord cwb-ord">S</span>
          <span className="cwb-col-name">
            <span className="cwb-name-line">
              {src.is_critical && <span className="cp-badge">CP</span>}
              {entry.milestone ? (
                <span className="cascade-milestone-tag">◆ Milestone</span>
              ) : (
                <>
                  <span className="cwb-swatch" style={{ backgroundColor: fill }} />
                  {wc && <span className="cwb-wc">{wc}</span>}
                </>
              )}
              <span className="cwb-name" title={src.task_name}>{src.task_name}</span>
            </span>
            <span className="cwb-name-tag">change under review</span>
          </span>
          <span className="cwb-col-date">
            <DateDelta oldVal={src.old_start} newVal={src.new_start} />
            <ConstraintBadge
              axis="start"
              cstrType={src.cstr_type}
              cstrDate={src.cstr_date}
              startDate={src.new_start}
              finishDate={src.new_end}
              violated={violatedIds.has(src.activity_id)}
              compact
            />
          </span>
          <span className="cwb-col-date">
            <DateDelta oldVal={src.old_end} newVal={src.new_end} />
            <ConstraintBadge
              axis="finish"
              cstrType={src.cstr_type}
              cstrDate={src.cstr_date}
              startDate={src.new_start}
              finishDate={src.new_end}
              violated={violatedIds.has(src.activity_id)}
              compact
            />
          </span>
          <span className="cwb-col-delta">
            <span className={`cwb-delta-chip${src.finish_delta < 0 ? " is-early" : ""}`}>
              {formatSignedDays(src.finish_delta)}
            </span>
          </span>
          <span className="cwb-col-dur cwb-dur-locked">—</span>
          <span className="cwb-col-preds">
            {src.preds.length === 0 ? (
              <span className="cwb-preds-muted">—</span>
            ) : (
              <span className="cwb-chips">
                {src.preds.map((pred) => (
                  <PredChip
                    key={pred.edge_id ?? `new-${pred.pred_activity_id}`}
                    row={src}
                    pred={pred}
                    colors={colors}
                    activityWorkCodeMap={activityWorkCodeMap}
                    zoneMap={zoneMap}
                    readOnly={readOnly}
                    edited={pred.edge_id != null && editedEdgeIds.has(pred.edge_id)}
                    onEdgeChange={onEdgeChange}
                    onEdgeRemove={onEdgeRemove}
                    onEdgeReplace={onEdgeReplace}
                    fetchExternals={fetchExternals}
                  />
                ))}
              </span>
            )}
          </span>
        </div>
      );
    }

    const row = entry.row!;
    const isContext = !!row.context;
    const locTag = [sectionMap.get(row.activity_id), zoneMap.get(row.activity_id)]
      .filter(Boolean)
      .join(" · ");
    return (
      <div
        key={row.activity_id}
        className={`cwb-row${isContext ? " cwb-row--context" : row.moved ? "" : " cwb-row--ontrack"}${selected ? " is-selected" : ""}`}
        ref={attachRef}
        onClick={handleRowClick(row.activity_id)}
      >
        <span className="cwb-col-ord cwb-ord">{isContext ? "↑" : row.order_index + 1}</span>
        <span className="cwb-col-name">
          <span className="cwb-name-line">
            {row.is_critical && <span className="cp-badge">CP</span>}
            {entry.milestone ? (
              <span className="cascade-milestone-tag">◆ Milestone</span>
            ) : (
              <>
                <span className="cwb-swatch" style={{ backgroundColor: fill }} />
                {wc && <span className="cwb-wc">{wc}</span>}
              </>
            )}
            <span className="cwb-name" title={row.task_name}>{row.task_name}</span>
          </span>
          {(locTag || isContext) && (
            <span className="cwb-name-tag">
              {[locTag, isContext ? "predecessor of the change" : ""].filter(Boolean).join(" · ")}
            </span>
          )}
          {row.gated_by && (
            <span
              className="cwb-name-tag cwb-gated"
              title={
                row.gated_by === "pin"
                  ? "An Expected Finish pin holds this finish — remaining duration flexes to meet it."
                  : "A Start-On-or-After holds this activity — no predecessor drives it to this date. Undo the push (or model it with a predecessor tie) to release it."
              }
            >
              {row.gated_by === "pin"
                ? "📌 held by pushed finish"
                : "→ held by pushed start (Start On or After)"}
            </span>
          )}
        </span>
        <span className="cwb-col-date">
          <DateDelta oldVal={row.old_start} newVal={row.new_start} />
          <ConstraintBadge
            axis="start"
            cstrType={row.cstr_type}
            cstrDate={row.cstr_date}
            startDate={row.new_start}
            finishDate={row.new_end}
            violated={violatedIds.has(row.activity_id)}
            compact
          />
        </span>
        <span className="cwb-col-date">
          <DateDelta oldVal={row.old_end} newVal={row.new_end} />
          <ConstraintBadge
            axis="finish"
            cstrType={row.cstr_type}
            cstrDate={row.cstr_date}
            startDate={row.new_start}
            finishDate={row.new_end}
            violated={violatedIds.has(row.activity_id)}
            compact
          />
        </span>
        <span className="cwb-col-delta">
          {isContext ? (
            <span className="cwb-preds-muted">—</span>
          ) : row.moved ? (
            <span className={`cwb-delta-chip${row.days_pushed < 0 ? " is-early" : ""}`}>
              {formatSignedDays(row.days_pushed)}
            </span>
          ) : (
            <span className="cwb-ontrack">on track ✓</span>
          )}
        </span>
        <span className="cwb-col-dur">
          <DurationCell
            days={row.duration_days}
            edited={editedDurationIds.has(row.activity_id)}
            disabled={readOnly || !row.duration_editable}
            onCommit={(d) => onDurationChange(row, d)}
          />
        </span>
        <span className="cwb-col-preds">
          <span className="cwb-chips">
            {row.preds.map((pred) => (
              <PredChip
                key={pred.edge_id ?? `new-${pred.pred_activity_id}`}
                row={row}
                pred={pred}
                colors={colors}
                activityWorkCodeMap={activityWorkCodeMap}
                zoneMap={zoneMap}
                readOnly={readOnly}
                edited={pred.edge_id != null && editedEdgeIds.has(pred.edge_id)}
                onEdgeChange={onEdgeChange}
                onEdgeRemove={onEdgeRemove}
                onEdgeReplace={onEdgeReplace}
                fetchExternals={fetchExternals}
              />
            ))}
          </span>
        </span>
      </div>
    );
  };

  const renderGanttEntry = (entry: Entry, i: number) => {
    if (!axis) return null;
    const wc = activityWorkCodeMap.get(entry.id);
    const fill = (wc && colors[wc]?.fill) || "#8a8a8a";

    const ghostX = dateX(axis, entry.oldStart);
    const ghostX2 = dateXEnd(axis, entry.oldEnd);
    const curX = dateX(axis, entry.newStart);
    const curX2 = dateXEnd(axis, entry.newEnd);
    const h = rowHeights[i] ?? FALLBACK_ROW_H;
    const isSource = entry.kind === "source";

    return (
      <div
        key={isSource ? "source" : entry.id}
        className={`cwb-gantt-row${isSource ? " cwb-gantt-row--source" : ""}${
          entry.row?.context ? " cwb-gantt-row--context" : entry.row && !entry.row.moved ? " cwb-gantt-row--ontrack" : ""
        }${selectedId === String(entry.id) ? " is-selected" : ""}`}
        style={{ height: h }}
        onClick={handleRowClick(entry.id)}
      >
        {entry.milestone ? (
          <>
            {ghostX != null && (
              <span className="cwb-gantt-diamond is-ghost" style={{ left: ghostX }}>◆</span>
            )}
            {curX != null && (
              <span className="cwb-gantt-diamond" style={{ left: curX, color: fill }}>◆</span>
            )}
          </>
        ) : (
          <>
            {ghostX != null && ghostX2 != null && (
              <div
                className="cwb-gantt-bar is-ghost"
                style={{ left: ghostX, width: Math.max(6, ghostX2 + DAY_PX - ghostX) }}
              />
            )}
            {curX != null && curX2 != null && (
              <div
                className="cwb-gantt-bar"
                style={{
                  left: curX,
                  width: Math.max(6, curX2 + DAY_PX - curX),
                  background: fill,
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const ddX = axis ? dateX(axis, result.data_date) : null;

  return (
    <div className="cwb-split">
      <div className="cwb-table-pane" ref={tablePaneRef}>
        <div className="cwb-rows-header">
          <span className="cwb-col-ord">#</span>
          <span className="cwb-col-name">Activity</span>
          <span className="cwb-col-date">Start</span>
          <span className="cwb-col-date">Finish</span>
          <span className="cwb-col-delta">Δ</span>
          <span className="cwb-col-dur">Dur</span>
          <span className="cwb-col-preds">Predecessors</span>
        </div>
        {entries.map(renderTableEntry)}
      </div>

      <div
        className="cwb-divider"
        title="Drag to resize the gantt"
        onPointerDown={onDividerPointerDown}
        onPointerMove={onDividerPointerMove}
        onPointerUp={onDividerPointerUp}
      >
        <span className="cwb-divider-grip" />
      </div>

      <div className="cwb-gantt-pane" style={{ width: ganttW }} ref={ganttPaneRef}>
        <div className="cwb-gantt-inner" style={{ width: innerW }}>
          <div className="cwb-gantt-header">
            {axis &&
              axis.days.map((d, i) =>
                new Date(d + "T00:00:00Z").getUTCDay() === 1 ? (
                  <span key={d} className="cwb-gantt-tick" style={{ left: i * DAY_PX }}>
                    {formatDateMonthDay(d)}
                  </span>
                ) : null,
              )}
          </div>
          <div className="cwb-gantt-body" style={{ height: rowTops.total }}>
            {ddX != null && <div className="cwb-gantt-dd" style={{ left: ddX }} />}
            {entries.map(renderGanttEntry)}
            {arrows.length > 0 && (
              <DepArrows
                arrows={arrows}
                width={innerW}
                height={rowTops.total}
                selectedId={selectedId}
                drivingOnly={false}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

WorkbenchRows.displayName = "WorkbenchRows";

export default React.memo(WorkbenchRows);
